// pending-0.1.19 bump for RSM CloseOut + MO escheatment port
/**
 * SdbInqService — AcctInq port for SDB (AcctType=50) with dual-element fetch
 * (`x_SafeDepBoxInfoRec` + `x_SafeDepAcctInfo`) from legacy SPFx.
 *
 * Source of truth:
 *   `C:\Projects\retail-account-opening\spfx-account-opening-v2\
 *    pcb-account-opening-v2\src\webparts\accountOpeningV2\components\
 *    screens\SdbManagementScreen.tsx:319-404` (handleLookup) and the
 *    SafeDepBoxSrch handling at :160-193 (loadVacant).
 *
 * Why dual-call:
 *   Same constraint as CD — jXchange filters the AcctInq response payload
 *   to the FIRST include element only. Asking for both extensions in a
 *   single call drops the second. Per JHA case 16782206 and reproduced
 *   against PCB Core Director 2025-06-11, we fire 2 parallel calls:
 *     Call 1 → `x_SafeDepBoxInfoRec` → box meta, branch, size, rent,
 *                                       key deposit, last access, access count
 *     Call 2 → `x_SafeDepAcctInfo`   → account title, status, RentPdToDt,
 *                                       restricted entry, related CIFs
 *
 * Route:
 *   `POST /jxrl/Deposit/AcctInq` via `ApiClient.jxrl.post('Deposit/AcctInq', ...)`
 *   (proxy route `jxrl/{*operation}` in `JxChangeProxyFunction.cs:22`).
 *
 * No fixtures. No hardcoded rental fees, penalty amounts, or abandonment
 * years. If Core doesn't return a field, we return `null` and the UI
 * renders "—".
 */
import type { ApiClient } from '@retail-ops/api-client';
import type { RawJxResponse } from '@retail-ops/shared-types';
import type { BoxDetail, BoxDetailRenter } from '../types';

// AcctType 50 = Safe Deposit Box in Core Director.
const SDB_ACCT_TYPE = '50';

type XtendElem = 'x_SafeDepBoxInfoRec' | 'x_SafeDepAcctInfo';

/** `#text: null` is how jXRL serializes missing string values. Drain to ''. */
function asText(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  if (v && typeof v === 'object') {
    const maybeText = (v as Record<string, unknown>)['#text'];
    if (maybeText == null) return '';
    return String(maybeText);
  }
  return v == null ? '' : String(v);
}

/** Parse a decimal string / number from jXRL; null if not a finite number. */
function asNumber(v: unknown): number | null {
  const s = asText(v).trim();
  if (!s) return null;
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * Normalize a Core Director date to ISO YYYY-MM-DD.
 * Core returns 8-char `YYYYMMDD`, already-ISO `YYYY-MM-DD`, or the
 * `0001-01-01` sentinel (= "no value"). Returns null for empties.
 */
function asIsoDate(v: unknown): string | null {
  const raw = asText(v).trim();
  if (!raw) return null;
  if (raw === '0001-01-01' || raw === '00010101') return null;
  if (/^\d{8}$/.test(raw)) {
    return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return null;
}

/** ISO timestamp (YYYY-MM-DDTHH:mm:ssZ). */
function asIsoTimestamp(v: unknown): string | null {
  const raw = asText(v).trim();
  if (!raw) return null;
  if (raw.startsWith('0001-01-01')) return null;
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) return d.toISOString();
  // Core Director sometimes returns YYYYMMDDHHMMSS
  if (/^\d{14}$/.test(raw)) {
    const iso = `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}T${raw.slice(8, 10)}:${raw.slice(10, 12)}:${raw.slice(12, 14)}Z`;
    const dd = new Date(iso);
    if (!Number.isNaN(dd.getTime())) return dd.toISOString();
  }
  return null;
}

/** Days between today (local midnight) and an ISO YYYY-MM-DD date. */
function dayDiff(iso: string, now: Date = new Date()): number {
  const target = new Date(`${iso}T00:00:00`).getTime();
  const today = new Date(`${now.toISOString().slice(0, 10)}T00:00:00`).getTime();
  return Math.round((target - today) / 86_400_000);
}

function getObj(src: unknown, key: string): Record<string, unknown> {
  if (!src || typeof src !== 'object') return {};
  const v = (src as Record<string, unknown>)[key];
  if (!v || typeof v !== 'object') return {};
  const maybeText = (v as Record<string, unknown>)['#text'];
  if (Object.keys(v as Record<string, unknown>).length === 1 && maybeText === null) return {};
  return v as Record<string, unknown>;
}

/**
 * Dig through the jXRL AcctInq envelope to find `SafeDepAcctInqRec`.
 * Core Director wraps under `AcctInqRs_MType.AcctRec_CType` in some
 * driver versions and flat on root in others (observed in production
 * probes — same dual-shape tolerance as CdInqService).
 */
function findSafeDepRec(resp: RawJxResponse | Record<string, unknown>): Record<string, unknown> {
  const root = resp as Record<string, unknown>;
  const mType = getObj(root, 'AcctInqRs_MType');
  const acctRec = Object.keys(mType).length ? getObj(mType, 'AcctRec_CType') : {};
  if (Object.keys(acctRec).length && acctRec['SafeDepAcctInqRec']) {
    return getObj(acctRec, 'SafeDepAcctInqRec');
  }
  if (root['SafeDepAcctInqRec']) {
    return getObj(root, 'SafeDepAcctInqRec');
  }
  if (root['AcctRec_CType']) {
    return getObj(getObj(root, 'AcctRec_CType'), 'SafeDepAcctInqRec');
  }
  return {};
}

/** Build jXRL AcctInq request for a given extension element. */
function buildAcctInqBody(acctId: string, xtendElem: XtendElem): Record<string, unknown> {
  return {
    InAcctId: { AcctId: acctId, AcctType: SDB_ACCT_TYPE },
    IncAcctInfo: true,
    IncXtendElemArray: {
      IncXtendElemInfo: { XtendElem: xtendElem },
    },
  };
}

/**
 * Extract a renter array from an assortment of Core response shapes.
 * Core can put co-owners under `SafeDepRenterArray`, `RenterArray`, or
 * `x_SafeDepAcctInfo.Renters` — we tolerate all three.
 */
function extractRenters(acctInfo: Record<string, unknown>, topLevel: Record<string, unknown>): ReadonlyArray<BoxDetailRenter> {
  const raw =
    acctInfo['RenterArray'] ??
    acctInfo['SafeDepRenterArray'] ??
    acctInfo['Renters'] ??
    topLevel['RenterArray'] ??
    topLevel['SafeDepRenterArray'] ??
    [];
  const arr = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return arr
    .map((r) => {
      const rec = (r ?? {}) as Record<string, unknown>;
      const pn = getObj(rec, 'PersonName');
      const first = asText(pn['FirstName']);
      const last = asText(pn['LastName']);
      const com = asText(pn['ComName']);
      const combined = [first, last].filter((x) => x.length).join(' ').trim();
      const name = (com || combined || asText(rec['Name']) || null) as string | null;
      const cifId = asText(rec['CustId']) || asText(rec['CifId']) || null;
      const relRaw = (asText(rec['RelCode']) || asText(rec['RelationshipCode']) || asText(rec['CustRelCode'])).trim().toUpperCase();
      const relCode = relRaw.length ? relRaw : null;
      const phone = asText(rec['Phone']) || asText(rec['PhoneNum']) || null;
      const addedDate = asIsoDate(rec['AddedDt']) ?? asIsoDate(rec['RelStartDt']);
      return { cifId, name, relationshipCode: relCode, phone, addedDate };
    })
    .filter((r) => r.cifId || r.name);
}

export interface FetchBoxDetailOptions {
  signal?: AbortSignal;
}

export class SdbInqService {
  public constructor(private readonly client: ApiClient) {}

  /**
   * Dual-call AcctInq + merge → BoxDetail.
   *
   * Partial-tolerance: if one call fails we still hydrate whatever the
   * other returned. This matches legacy SPFx behavior and keeps officers
   * moving when Core returns a partial response.
   */
  public async fetchBoxDetail(acctId: string, opts: FetchBoxDetailOptions = {}): Promise<BoxDetail> {
    if (!acctId) throw new Error('acctId is required');

    const { signal } = opts;
    const postOpts = signal ? { signal } : undefined;

    const call1 = this.client.jxrl.post<Record<string, unknown>, RawJxResponse>(
      'Deposit/AcctInq',
      buildAcctInqBody(acctId, 'x_SafeDepBoxInfoRec'),
      postOpts,
    );
    const call2 = this.client.jxrl.post<Record<string, unknown>, RawJxResponse>(
      'Deposit/AcctInq',
      buildAcctInqBody(acctId, 'x_SafeDepAcctInfo'),
      postOpts,
    );

    const [r1Raw, r2Raw] = await Promise.allSettled([call1, call2]);
    const r1 = r1Raw.status === 'fulfilled' ? r1Raw.value : ({} as RawJxResponse);
    const r2 = r2Raw.status === 'fulfilled' ? r2Raw.value : ({} as RawJxResponse);

    if (r1Raw.status === 'rejected' && r2Raw.status === 'rejected') {
      throw r1Raw.reason instanceof Error ? r1Raw.reason : new Error('SDB AcctInq failed');
    }

    return SdbInqService.merge(acctId, r1, r2);
  }

  /**
   * Merge the two AcctInq responses → `BoxDetail`.
   * Exposed as static for testability (feed canned JSON fixtures from probes).
   */
  public static merge(acctId: string, resp1: RawJxResponse, resp2: RawJxResponse): BoxDetail {
    // --- Call 1 — x_SafeDepBoxInfoRec (box/physical meta) ---
    const sdRec1 = findSafeDepRec(resp1);
    const xBox = getObj(sdRec1, 'x_SafeDepBoxInfoRec');
    const boxInfo = getObj(xBox, 'SafeDepBoxInfoRec');
    // Some drivers put the box info directly on x_ wrapper.
    const boxSrc = Object.keys(boxInfo).length ? boxInfo : xBox;

    // --- Call 2 — x_SafeDepAcctInfo (account/title/renters) ---
    const sdRec2 = findSafeDepRec(resp2);
    const xAcct = getObj(sdRec2, 'x_SafeDepAcctInfo');
    const acctInfo = getObj(xAcct, 'SafeDepAcctInfo');
    const acctSrc = Object.keys(acctInfo).length ? acctInfo : xAcct;

    // --- Box physical meta ---
    const boxNumber =
      asText(boxSrc['BoxNumber']) ||
      asText(boxSrc['BoxNum']) ||
      asText(boxSrc['SafeDepBoxRef']) ||
      asText(sdRec1['BoxNumber']) ||
      acctId ||
      null;
    const branchId =
      asText(boxSrc['BrCode']) ||
      asText(sdRec1['BrCode']) ||
      asText(acctSrc['BrCode']) ||
      asText(sdRec2['BrCode']) ||
      null;
    const productCode =
      asText(boxSrc['ProdCode']) ||
      asText(acctSrc['ProdCode']) ||
      asText(sdRec1['ProdCode']) ||
      null;
    const sizeCode =
      asText(boxSrc['ProdDesc']) ||
      asText(boxSrc['BoxSize']) ||
      asText(boxSrc['BoxDesc']) ||
      asText(acctSrc['SafeDepBoxRef']) ||
      asText(acctSrc['BoxSize']) ||
      null;
    const rentalFeeDue =
      asNumber(boxSrc['RentAmt']) ??
      asNumber(acctSrc['RentAmt']) ??
      asNumber(sdRec1['RentAmt']);
    const keyDeposit =
      asNumber(boxSrc['KeyDepAmt']) ??
      asNumber(acctSrc['KeyDepAmt']) ??
      asNumber(boxSrc['KeyDeposit']);
    const lastAccessDate =
      asIsoTimestamp(boxSrc['LastAccessDt']) ??
      asIsoTimestamp(boxSrc['LastAccessedAt']) ??
      asIsoTimestamp(sdRec1['LastAccessDt']);
    const accessCount =
      asNumber(boxSrc['AccessCnt']) ??
      asNumber(boxSrc['AccessCount']) ??
      asNumber(sdRec1['AccessCnt']);

    // --- Account-side meta ---
    const status = asText(acctSrc['AcctStat']) || asText(sdRec2['AcctStat']) || asText(sdRec1['AcctStat']) || null;
    const primaryCustomerName =
      asText(acctSrc['AcctTitle']) ||
      asText(sdRec2['AcctTitle']) ||
      asText(sdRec1['AcctTitle']) ||
      null;
    const primaryCifId =
      asText(acctSrc['CustId']) ||
      asText(sdRec2['CustId']) ||
      asText(sdRec1['CustId']) ||
      null;
    const rentPaidToDate =
      asIsoDate(acctSrc['RentPdToDt']) ??
      asIsoDate(sdRec2['RentPdToDt']) ??
      asIsoDate(sdRec1['RentPdToDt']);
    const restrictedEntryCode = asText(acctSrc['RstrEntryCode']) || asText(sdRec2['RstrEntryCode']) || null;
    const restrictedEntry = Boolean(restrictedEntryCode && restrictedEntryCode.trim().length && restrictedEntryCode.trim().toUpperCase() !== 'N');

    // --- Fields 133 / 157 balances, 38 vacant flag, 801 keys-missing ---
    const balance =
      asNumber(acctSrc['RentBalance']) ??
      asNumber(boxSrc['Field133']) ??
      asNumber(acctSrc['Field133']) ??
      asNumber(acctSrc['CurBal']);
    const lateFee =
      asNumber(acctSrc['LateFeeAmt']) ??
      asNumber(boxSrc['Field157']) ??
      asNumber(acctSrc['Field157']);

    const vacantFlagRaw = asText(acctSrc['Field38']) || asText(boxSrc['Field38']) || asText(acctSrc['BoxOccupType']);
    const vacant = vacantFlagRaw.trim().toUpperCase() === 'VACANT' || vacantFlagRaw.trim().toUpperCase() === 'V' ||
      vacantFlagRaw.trim().toUpperCase() === 'UNOCCUP' ||
      (status ?? '').trim().toUpperCase() === 'VACANT';

    const keysMissingRaw = asText(acctSrc['Field801']) || asText(boxSrc['Field801']);
    const keysMissing = keysMissingRaw.trim().toUpperCase() === 'Y' ||
      keysMissingRaw.trim().toUpperCase() === 'TRUE' ||
      keysMissingRaw.trim() === '1';

    // --- Next-due + overdue days ---
    let nextDueDate: string | null = null;
    if (rentPaidToDate) {
      const d = new Date(`${rentPaidToDate}T00:00:00`);
      d.setFullYear(d.getFullYear() + 1);
      nextDueDate = d.toISOString().slice(0, 10);
    } else {
      nextDueDate = asIsoDate(acctSrc['NextDueDt']) ?? asIsoDate(boxSrc['NextDueDt']);
    }

    let daysOverdue: number | null = null;
    if (nextDueDate) {
      const diff = dayDiff(nextDueDate);
      daysOverdue = diff < 0 ? Math.abs(diff) : 0;
    }

    // --- Renters ---
    const renters = extractRenters(acctSrc, sdRec2);

    return {
      acctId,
      boxNumber: boxNumber || null,
      branchId,
      productCode,
      sizeCode,
      rentalFeeDue,
      rentPaidToDate,
      nextDueDate,
      balance,
      lateFee,
      keyDeposit,
      lastAccessDate,
      accessCount,
      renters,
      primaryCifId,
      primaryCustomerName,
      status,
      restrictedEntry,
      restrictedEntryCode,
      keysMissing,
      vacant,
      daysOverdue,
    };
  }
}

/** Factory for callers that only have the ApiClient (no DI container). */
export function createSdbInqService(client: ApiClient): SdbInqService {
  return new SdbInqService(client);
}
